import {
  type ChangeEvent,
  type FC,
  useCallback,
  useEffect,
  useMemo,
} from 'react'

import { CreatorPermission, FeatureFlagName, TeamMemberRole } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUnit } from 'effector-react'
import debounce from 'lodash/debounce'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Toggle } from '@/components/ui/Toggle.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { FAN_NUMBERING_MAX, FAN_NUMBERING_MIN } from '@/consts/fan-numbering'
import { $account } from '@/models/account.ts'
import { $currentCreator } from '@/models/creators.ts'
import { $availableFeatures } from '@/models/feature-flags.ts'
import { CreatorFeatures } from '@/modules/accounts/types'
import { SettingsPageTitle } from '@/modules/creator-settings/SettingsPageTitle'
import { DEFAULT_FAN_NUMBER } from '@/modules/creator-settings/constants'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption.tsx'
import { MultiSelect } from '@/modules/shifts/ui/dialogs/multi-select'
import { useFetchTeamTable } from '@/modules/team/hooks/use-fetch-team-table.tsx'
import { getErrorMessage } from '@/utils/error.ts'
import { isFeatureAvailable as isFeatureAvailableUtil } from '@/utils/featureFlags.ts'
import { hasPermission, preventDefaultEvent } from '@/utils/helpers'

import { useAutomationsMutations } from '../../hooks/use-automations-mutations'
import {
  $inboxSplitterStatus,
  updateInboxSplitterStatusFx,
} from '../../models/fan-numbering'
import { $supervisors, updateSupervisors } from '../../models/supervisors'
import { FanNumberingSettings } from '../components/fan-numbering-settings'
import { NumberAssignmentsTable } from '../components/number-assignments-table'

export type FanNumberingFormValues = z.infer<typeof FanNumberingFormSchema>

interface Props {}

export const FanNumberingFormSchema = z.object({
  enabled: z.boolean(),
  maxNumbers: z
    .number()
    .min(FAN_NUMBERING_MIN, {
      message: `The maximum number cannot be below ${FAN_NUMBERING_MIN}`,
    })
    .max(FAN_NUMBERING_MAX, {
      message: `You can only number your fans until ${FAN_NUMBERING_MAX}`,
    }),
})

export const FanNumberingForm: FC<Props> = () => {
  const { enableFanNumbering, setFanNumberingMaxNums } =
    useAutomationsMutations()
  const activeCreator = useUnit($currentCreator)
  const account = useUnit($account)
  const {
    setSettingsData,
    maxFanNumber: maxFanNumberFromSetting,
    fanNumberingEnabled,
  } = useSettingsContext()
  const supervisors = useUnit($supervisors)
  const inboxSplitterStatus = useUnit($inboxSplitterStatus)
  const availableFeatures = useUnit($availableFeatures)
  const isAdminOrLeader = [TeamMemberRole.ADMIN, TeamMemberRole.LEADER].some(
    (role) => role === account?.memberRole,
  )
  const isFeatureAvailable = isFeatureAvailableUtil(
    availableFeatures,
    FeatureFlagName.INBOX_SPLITTER_FEATURE,
  )

  const hasProfessional = activeCreator?.plan.startsWith('professional')

  const toast = useToast()

  const { data: teamMembers, isFetching } = useFetchTeamTable()

  const defaultFormValues = useMemo(
    () => ({
      enabled: fanNumberingEnabled,
      maxNumbers: maxFanNumberFromSetting || DEFAULT_FAN_NUMBER,
    }),
    [fanNumberingEnabled, maxFanNumberFromSetting],
  )

  const form = useForm<FanNumberingFormValues>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: zodResolver(FanNumberingFormSchema),
  })

  const { control, reset, watch } = form
  const enabled = watch('enabled')

  useEffect(() => {
    reset(defaultFormValues)
  }, [reset, defaultFormValues])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdateMaxNumbers = useCallback(
    debounce(async (maxFanNumber: number) => {
      if (
        maxFanNumber > FAN_NUMBERING_MAX ||
        maxFanNumber < FAN_NUMBERING_MIN ||
        maxFanNumber === maxFanNumberFromSetting
      ) {
        return
      }
      await setFanNumberingMaxNums.mutateAsync({
        max: maxFanNumber,
      })

      setSettingsData((prevState) => ({
        ...prevState,
        maxFanNumber,
      }))
    }, 1000),
    [maxFanNumberFromSetting],
  )

  const handleChangeMaxNumber = useCallback(debouncedUpdateMaxNumbers, [
    debouncedUpdateMaxNumbers,
  ])

  const handleEnabledChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const checkedState = event.target.checked

      await enableFanNumbering.mutateAsync({ state: checkedState })

      setSettingsData((prevState) => ({
        ...prevState,
        fanNumberingEnabled: checkedState,
      }))

      toast.success(
        `Successfully ${checkedState ? 'enabled' : 'disabled'} Fan Numbering`,
      )
    },
    [enableFanNumbering, setSettingsData, toast],
  )

  const handleInboxSplitterChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      updateInboxSplitterStatusFx(event.target.checked)
    },
    [],
  )

  const hasFullPermission = hasPermission(
    activeCreator!,
    CreatorFeatures.fan_numbering,
    CreatorPermission.MODIFY_FAN_NUMBERING,
  )

  const teamMemberOptions = useMemo(
    () =>
      teamMembers?.map((member) => ({
        label: member.name!,
        value: String(member.userId),
        disabled: member.role !== TeamMemberRole.MEMBER || !isAdminOrLeader,
        forcedSelected: member.role !== TeamMemberRole.MEMBER,
      })) || [],
    [teamMembers, isAdminOrLeader],
  )

  const selectedSupervisors = supervisors.map((supervisor) =>
    String(supervisor.userId),
  )

  const handleSupervisorChange = (value: string[]) => {
    updateSupervisors(value.map(Number))
  }

  const teamMembersForAssigment =
    teamMembers?.filter(
      (member) => !supervisors.some((s) => s.id === member.id),
    ) || []

  return (
    <FormProvider {...form}>
      <form
        className="w-full"
        onSubmit={preventDefaultEvent}
      >
        <Page
          title="Fan Numbering"
          description={
            <div className="flex items-center gap-1">
              <span>
                With the Fan Numbering Feature you can assign numbers to your
                chats for your chatters to stay organized and have an individual
                number to chat by.
              </span>
            </div>
          }
          headerOptions={
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <Toggle
                  disabled={!hasFullPermission}
                  name={field.name}
                  checked={field.value}
                  onChange={(value) => {
                    handleEnabledChange(value).then(() => field.onChange(value))
                  }}
                />
              )}
            />
          }
        >
          {enabled && (
            <div className="flex flex-col gap-8">
              <FanNumberingSettings
                isLoading={setFanNumberingMaxNums.isLoading}
                hasFullPermission={hasFullPermission}
                onChange={handleChangeMaxNumber}
              />
              {isFeatureAvailable && isAdminOrLeader && hasProfessional && (
                <>
                  <Separator />
                  <SettingsPageTitle
                    title="Inbox Splitter"
                    description={
                      <span className="flex items-center gap-2">
                        Inbox Splitter segments fan interactions by assigning
                        chatters to specific numbers, ensuring organized and
                        efficient inbox management
                      </span>
                    }
                    toggleName=""
                    enabled={!!inboxSplitterStatus?.inboxSplitterEnabled}
                    onChange={handleInboxSplitterChange}
                    hideLabel
                  />
                </>
              )}

              {inboxSplitterStatus?.inboxSplitterEnabled &&
                hasProfessional &&
                isAdminOrLeader && (
                  <>
                    <Separator />

                    <SettingsOption
                      title="Supervisors"
                      description="Select Supervisors to grant users access to all fan numbers"
                      mobileVertical
                      biggerTitle
                      disableTopPadding
                      disableBottomPadding
                    >
                      <div className="flex flex-col items-start gap-2 md:items-end">
                        <MultiSelect
                          disabled={isFetching || !hasFullPermission}
                          value={selectedSupervisors}
                          onChange={handleSupervisorChange}
                          options={teamMemberOptions}
                          entity="Team Members"
                          placeholder="Assign Team Members"
                          searchPlaceholder="Search Team Member"
                          // className="w-52"
                          disableAllSelect
                        />
                      </div>
                    </SettingsOption>

                    <Separator />

                    <NumberAssignmentsTable
                      maxNumbers={maxFanNumberFromSetting}
                      teamMembers={teamMembersForAssigment}
                      disabled={!hasFullPermission || isFetching}
                      ableToEdit={isAdminOrLeader}
                    />
                  </>
                )}
            </div>
          )}
        </Page>
      </form>
    </FormProvider>
  )
}
